import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Croissant, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export default function LoginPage() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Role-based landing route
  const landingFor = (u) => {
    if (!u) return "/app/dashboard";
    const role = u.role || u.role_id;
    // Branch-scoped factory roles land on their unit dashboard directly
    if (role === "factory_manager" || role === "factory_admin") {
      return "/app/mfg/factory-dashboard";
    }
    // Showroom-scoped retail roles land on their store dashboard
    if (role === "showroom_admin" || role === "showroom_sales_manager") {
      return "/app/store-dashboard";
    }
    // QC-only roles land straight on Lab & QC
    if (role === "qc_manager" || role === "qc_staff") {
      return "/app/qc";
    }
    if (role === "cashier") return "/app/pos";
    if (role === "delivery_staff") return "/m/home";
    return "/app/dashboard";
  };

  React.useEffect(() => {
    if (user) navigate(landingFor(user), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      // `login()` returns the user object; fall back to `user` state if not returned
      const u = (result && result.user) || result || user;
      toast.success("Welcome to Bakemill ERP");
      navigate(landingFor(u), { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FDFBF7]">
      {/* Left panel */}
      <div className="hidden md:flex login-hero relative flex-col justify-between p-10 text-white">
        <img
          src="/bakemill-logo-full.webp"
          alt="Bakemill · Sweet moments of life"
          className="h-16 w-auto object-contain rounded-md"
          data-testid="login-brand-logo"
        />

        <div className="max-w-md space-y-4">
          <div className="uc-label !text-[#F6AD55]">Enterprise ERP + CRM</div>
          <h1 className="font-[Outfit] text-4xl lg:text-5xl font-semibold tracking-tight">
            Bake the future.<br />
            Sell it smarter.
          </h1>
          <p className="text-white/80 text-sm leading-relaxed">
            End-to-end management for cakes, cookies and artisan bakery operations —
            from lead capture and quotations to GST-ready invoices and
            employee performance.
          </p>
        </div>

        <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">
          © {new Date().getFullYear()} Bakemill Foods Pvt Ltd
        </div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          className="w-full max-w-md space-y-6"
          data-testid="login-form"
        >
          <div className="space-y-1.5">
            <div className="uc-label">Sign in</div>
            <h2 className="font-[Outfit] text-3xl font-semibold tracking-tight">
              Welcome back
            </h2>
            <p className="text-sm text-[#5C4033]">
              Use your Bakemill credentials to continue.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[#2C1810]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1.5 bg-white border-[#EAE5D9] focus-visible:ring-[#C05621]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#2C1810]">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1.5 bg-white border-[#EAE5D9] focus-visible:ring-[#C05621]"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full bg-[#C05621] hover:bg-[#9C4221] text-white h-11"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
